import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { PORTFOLIO_PROJECTS, CATEGORIES, Project, ProjectCategory } from '../../mocks/portfolio';
import ProjectCard from './components/ProjectCard';
import ProjectModal from './components/ProjectModal';
import SchemaJsonLd from '../../components/base/SchemaJsonLd';
import Navbar from '../home/components/Navbar';
import PageHero from '../../components/feature/PageHero';
import Footer from '../home/components/Footer';

const PORTFOLIO_HERO_IMAGE =
  'https://public.readdy.ai/ai/img_res/8e000730b3c5d66c6a656ce60db2d4a6.jpg';

const CATEGORY_ICONS: Record<string, string> = {
  'Building & Facility Maintenance': 'ri-building-2-line',
  'Residential Remodeling': 'ri-home-heart-line',
  'Commercial Remodeling': 'ri-store-2-line',
  'General Maintenance': 'ri-hammer-line',
};

const CATEGORY_SHORT: Record<string, string> = {
  'Building & Facility Maintenance': 'Building & Facility',
  'Residential Remodeling': 'Residential',
  'Commercial Remodeling': 'Commercial',
  'General Maintenance': 'Maintenance',
};

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://fmacrew.com';

const portfolioSchema = {
  '@context': 'https://schema.org',
  '@type': 'CollectionPage',
  name: 'FMA Crew Project Portfolio — Multi-Family & Apartment Maintenance Atlanta GA',
  description:
    'Browse completed projects across multi-family & apartment maintenance, unit turns, balcony restoration, waterproofing, residential remodeling, and commercial build-outs in Greater Atlanta, GA.',
  url: `${SITE_URL}/portfolio`,
  isPartOf: {
    '@type': 'WebSite',
    name: 'FMA Crew',
    url: SITE_URL,
  },
  about: {
    '@type': 'LocalBusiness',
    name: 'FMA Crew',
    url: SITE_URL,
  },
  breadcrumb: {
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_URL },
      { '@type': 'ListItem', position: 2, name: 'Portfolio', item: `${SITE_URL}/portfolio` },
    ],
  },
};

export default function Portfolio() {
  const { t } = useTranslation();
  const [activeFilter, setActiveFilter] = useState<ProjectCategory | 'All'>('All');
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);

  const filtered = useMemo(
    () =>
      activeFilter === 'All'
        ? PORTFOLIO_PROJECTS
        : PORTFOLIO_PROJECTS.filter((p) => p.category === activeFilter),
    [activeFilter]
  );

  return (
    <>
      <title>FMA Crew Project Portfolio | Building Maintenance &amp; Remodeling Atlanta GA</title>
      <meta
        name="description"
        content="View FMA Crew's completed projects — building & facility maintenance, residential remodeling, commercial build-outs across Greater Atlanta. Licensed & Insured contractor in Norcross, GA."
      />
      <meta
        name="keywords"
        content="building maintenance Atlanta portfolio, residential remodeling projects Norcross GA, commercial remodeling Atlanta, FMA Crew projects"
      />
      <link rel="canonical" href={`${SITE_URL}/portfolio`} />
      <meta property="og:title" content="FMA Crew Project Portfolio | Building Maintenance & Remodeling Atlanta GA" />
      <meta property="og:description" content="Browse real completed projects across building maintenance, residential remodeling, and commercial build-outs in Greater Atlanta." />
      <meta property="og:type" content="website" />
      <meta property="og:url" content={`${SITE_URL}/portfolio`} />
      <SchemaJsonLd schema={portfolioSchema} id="schema-portfolio" />

      <main className="min-h-screen bg-slate-50">
        <Navbar />

        <PageHero
          title={t('portfolio.hero.title')}
          titleEm={t('portfolio.hero.titleEm')}
          image={PORTFOLIO_HERO_IMAGE}
        />

        {/* Filter + Grid */}
        <section className="py-8 md:py-14">
          <div className="max-w-7xl mx-auto px-4 md:px-8">
            {/* Coming Soon Notice Banner */}
            <div className="mb-6 md:mb-8 flex items-start gap-3 px-4 py-4 bg-amber-50 border border-amber-200 rounded-xl">
              <div className="w-8 h-8 flex items-center justify-center rounded-full bg-amber-100 text-amber-600 shrink-0 mt-0.5">
                <i className="ri-information-line text-base" />
              </div>
              <div>
                <p className="text-amber-800 font-semibold text-sm">Portfolio Coming Soon</p>
                <p className="text-amber-700 text-xs leading-relaxed mt-0.5">
                  We are currently building our project gallery. Real case studies and photos will be added here as projects are completed. Stay tuned!
                </p>
              </div>
            </div>

            {/* Filter bar */}
            <div className="overflow-x-auto pb-1 -mx-4 px-4 md:mx-0 md:px-0 mb-6 md:mb-10">
              <div className="flex items-center gap-2 md:gap-3 w-max md:w-auto md:flex-wrap">
                <button
                  onClick={() => setActiveFilter('All')}
                  className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-xs md:text-sm font-semibold transition-all duration-200 whitespace-nowrap cursor-pointer ${
                    activeFilter === 'All'
                      ? 'bg-[#05294A] text-white'
                      : 'bg-white border border-slate-200 text-slate-600 hover:border-[#92D7E8]'
                  }`}
                >
                  <i className="ri-apps-2-line" />
                  All
                  <span
                    className={`text-xs px-1.5 py-0.5 rounded-full font-bold ${
                      activeFilter === 'All' ? 'bg-white/20 text-white' : 'bg-slate-100 text-slate-500'
                    }`}
                  >
                    {PORTFOLIO_PROJECTS.length}
                  </span>
                </button>

                {CATEGORIES.map((cat) => {
                  const count = PORTFOLIO_PROJECTS.filter((p) => p.category === cat).length;
                  const isActive = activeFilter === cat;
                  return (
                    <button
                      key={cat}
                      onClick={() => setActiveFilter(cat)}
                      className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-xs md:text-sm font-semibold transition-all duration-200 whitespace-nowrap cursor-pointer ${
                        isActive
                          ? 'bg-[#05294A] text-white'
                          : 'bg-white border border-slate-200 text-slate-600 hover:border-[#92D7E8]'
                      }`}
                    >
                      <div className="w-4 h-4 flex items-center justify-center">
                        <i className={`${CATEGORY_ICONS[cat]} text-xs md:text-sm`} />
                      </div>
                      <span className="md:hidden">{CATEGORY_SHORT[cat] ?? cat}</span>
                      <span className="hidden md:inline">{cat}</span>
                      <span
                        className={`text-xs px-1.5 py-0.5 rounded-full font-bold ${
                          isActive ? 'bg-white/20 text-white' : 'bg-slate-100 text-slate-500'
                        }`}
                      >
                        {count}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Results count */}
            <div className="flex items-center justify-between mb-5">
              <p className="text-slate-500 text-xs md:text-sm">
                Showing{' '}
                <span className="font-semibold text-slate-900">{filtered.length}</span>{' '}
                project{filtered.length !== 1 ? 's' : ''}
                {activeFilter !== 'All' && (
                  <span className="hidden sm:inline">
                    {' '}in{' '}
                    <span className="font-semibold text-[#05294A]">{activeFilter}</span>
                  </span>
                )}
              </p>
              {activeFilter !== 'All' && (
                <button
                  onClick={() => setActiveFilter('All')}
                  className="flex items-center gap-1 text-xs text-slate-400 hover:text-[#05294A] transition-colors cursor-pointer"
                >
                  <i className="ri-close-circle-line" />
                  Clear
                </button>
              )}
            </div>

            {/* Project grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
              {filtered.map((project) => (
                <ProjectCard key={project.id} project={project} onOpen={() => {}} />
              ))}
            </div>

            {/* Empty state */}
            {filtered.length === 0 && (
              <div className="text-center py-20">
                <div className="w-16 h-16 flex items-center justify-center rounded-full bg-slate-100 text-slate-400 mx-auto mb-4">
                  <i className="ri-search-line text-2xl" />
                </div>
                <p className="text-slate-500 text-base">No projects found in this category.</p>
              </div>
            )}
          </div>
        </section>

        {/* CTA Banner */}
        <section className="bg-[#05294A] py-12 md:py-16">
          <div className="max-w-7xl mx-auto px-4 md:px-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-6 md:gap-8">
            <div>
              <h3 className="text-2xl md:text-3xl font-bold text-white mb-2">
                Ready to Start Your Project?
              </h3>
              <p className="text-slate-400 text-sm md:text-base">
                Get a free, no-obligation estimate from the FMA Crew team within 24 hours.
              </p>
            </div>
            <Link
              to="/contact"
              className="flex items-center gap-2 px-6 py-3 md:px-8 md:py-4 bg-[#67CBFF] text-[#05294A] rounded-lg font-semibold text-sm hover:bg-[#92D7E8] transition-colors whitespace-nowrap cursor-pointer shrink-0 w-full md:w-auto justify-center md:justify-start"
            >
              <span>Get a Free Estimate</span>
              <i className="ri-arrow-right-line" />
            </Link>
          </div>
        </section>

        <Footer />

        {/* Project Modal */}
        <ProjectModal project={selectedProject} onClose={() => setSelectedProject(null)} />
      </main>
    </>
  );
}
